import React, { createContext, useCallback, useContext } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import {
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  type RedirectRequest,
  type SilentRequest
} from '@azure/msal-browser'
import { FetchError, removeLocalStorageItem } from '../index'
import { paths as pathsProveV1 } from '../types/prove-v1'
import { paths as pathsProveV2 } from '../types/prove-v2'
import { paths as pathsProveV3 } from '../types/prove-v3'
import { paths as pathsProveV4 } from '../types/prove-v4'
import { paths as pathsLabV2 } from '../types/lab-v2'
import createClient, { Client, Middleware } from 'openapi-fetch'
import { MediaType } from 'openapi-typescript-helpers'
import { useActiveAccount } from './useActiveAccount'
import {
  fetchWithAuthRetry as fetchWithAuthRetryImpl,
  parseFetchError
} from './authFetchRetry'

type PathsByVersion<V extends string> = V extends 'prove-v1'
  ? pathsProveV1
  : V extends 'prove-v2'
    ? pathsProveV2
    : V extends 'prove-v3'
      ? pathsProveV3
      : V extends 'prove-v4'
        ? pathsProveV4
        : V extends 'lab-v2'
          ? pathsLabV2
          : never

export interface IAuthContext {
  loginRequest: RedirectRequest
  logout: () => Promise<void>
  isAuthenticated: boolean
  accountName?: string
  isAdmin?: boolean
  roles?: string[]
  permissions?: import('../types/prove-v1').components['schemas']['UserPermission'][]
  accessPending?: boolean
  accessError?: boolean
  accessRefreshing?: boolean
  retryAccess?: () => Promise<unknown>
  userId?: string
  getAccessToken: (forceRefresh?: boolean) => Promise<string>
  authenticatedFetch: (
    input: RequestInfo | URL,
    init?: RequestInit
  ) => Promise<Response>
  authFetch: () => Client<PathsByVersion<'prove-v1'>, MediaType>
  authFetchV2: () => Client<PathsByVersion<'prove-v2'>, MediaType>
  authFetchV3: () => Client<PathsByVersion<'prove-v3'>, MediaType>
  authFetchV4: () => Client<PathsByVersion<'prove-v4'>, MediaType>
  authFetchLabV2: () => Client<PathsByVersion<'lab-v2'>, MediaType>
}

const AuthContext = createContext<IAuthContext | undefined>(undefined)
const noRoles: string[] = []
const noPermissions: NonNullable<IAuthContext['permissions']> = []

const AuthProvider: React.FC<{
  children: React.ReactNode
  loginRequest: RedirectRequest
  baseUrl: string
}> = (props) => {
  const queryClient = useQueryClient()
  const { instance, accounts, inProgress } = useMsal()
  const account = useActiveAccount(instance, accounts)

  const isAuthenticated = useIsAuthenticated()
  const accountName = account?.name
  const userId = account?.idTokenClaims?.oid
  const postLogoutRedirectUri = `${globalThis.location.origin}/login`

  const clearAuthState = useCallback((): void => {
    queryClient.clear()
    removeLocalStorageItem('REACT_QUERY_OFFLINE_CACHE')
    removeLocalStorageItem('application-settings')
  }, [queryClient])

  const logout = useCallback(async (): Promise<void> => {
    clearAuthState()
    await instance.logoutRedirect({ postLogoutRedirectUri })
  }, [clearAuthState, instance, postLogoutRedirectUri])
  const pendingRequestsRef = React.useRef(new Map<string, Request>())

  const buildBaseRequest = useCallback((): SilentRequest => {
    if (!isAuthenticated) {
      throw new Error('Not authenticated')
    }
    if (!account) {
      throw new Error('No active account')
    }

    return {
      ...props.loginRequest,
      account
    }
  }, [isAuthenticated, account, props.loginRequest])

  const getAccessToken = useCallback(
    async (forceRefresh = false): Promise<string> => {
      const request = buildBaseRequest()
      let tokenResponse
      try {
        tokenResponse = await instance.acquireTokenSilent({
          ...request,
          forceRefresh
        })
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          if (inProgress === InteractionStatus.None) {
            await instance.acquireTokenRedirect(request)
            // acquireTokenRedirect normally navigates away; this throw is
            // only reached in test environments where it is mocked.
            throw error
          }
          // An interaction (e.g. a redirect) is already in progress – wait for
          // it to complete (signalled by a success event), then retry silently.
          return new Promise<string>((resolve, reject) => {
            const callbackId = instance.addEventCallback((event) => {
              if (
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                event.eventType === EventType.LOGIN_SUCCESS
              ) {
                instance.removeEventCallback(callbackId ?? '')
                getAccessToken(forceRefresh).then(resolve, reject)
              }
            })
          })
        }
        throw error
      }
      if (!tokenResponse.accessToken) {
        throw new Error('Failed to get access token')
      }
      return tokenResponse.accessToken
    },
    [buildBaseRequest, inProgress, instance]
  )

  const fetchWithAuthRetry = useCallback(
    async (request: Request, response?: Response): Promise<Response> => {
      if (import.meta.env.MODE === 'test') {
        return response ?? (await fetch(request))
      }

      return fetchWithAuthRetryImpl(request, { getAccessToken }, response)
    },
    [getAccessToken]
  )

  const authenticatedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const request = new Request(input, init)
      const response = await fetchWithAuthRetry(request)
      if (!response.ok) await parseFetchError(response)
      return response
    },
    [fetchWithAuthRetry, parseFetchError]
  )

  const fetchClientMiddleware: Middleware = React.useMemo(
    () => ({
      async onRequest({ request, id }) {
        if (import.meta.env.MODE !== 'test') {
          const token = await getAccessToken()
          request.headers.set('Authorization', `Bearer ${token}`)
          // Stash a pristine clone so the request can be retried once if the
          // server rejects a just-expired token with 401.
          pendingRequestsRef.current.set(id, request.clone())
        }
        return request
      },
      async onResponse({ response, id }) {
        let finalResponse = response
        const original = pendingRequestsRef.current.get(id)
        pendingRequestsRef.current.delete(id)

        if (response.status === 401 && original) {
          finalResponse = await fetchWithAuthRetry(original.clone(), response)
        }

        if (!finalResponse.ok) {
          await parseFetchError(finalResponse)
        }
        return finalResponse
      },
      async onError({ error, id }) {
        pendingRequestsRef.current.delete(id)
        if (error instanceof Error) {
          const status =
            'status' in error && typeof error.status === 'number'
              ? error.status
              : undefined
          const detail =
            'detail' in error && typeof error.detail === 'string'
              ? error.detail
              : undefined
          return new FetchError(error.message, status, detail)
        } else {
          return new FetchError('Unknown error')
        }
      }
    }),
    [fetchWithAuthRetry, getAccessToken, parseFetchError]
  )

  const authFetchVersion = useCallback(<V extends string>() => {
    const client = createClient<PathsByVersion<V>>({
      baseUrl: props.baseUrl
    })
    client.use(fetchClientMiddleware)
    return client
  }, [props.baseUrl, fetchClientMiddleware])

  // Convenience wrappers
  const authFetch = useCallback(
    () => authFetchVersion<'prove-v1'>(),
    [authFetchVersion]
  )
  const authFetchV2 = useCallback(
    () => authFetchVersion<'prove-v2'>(),
    [authFetchVersion]
  )
  const authFetchV3 = useCallback(
    () => authFetchVersion<'prove-v3'>(),
    [authFetchVersion]
  )
  const authFetchV4 = useCallback(
    () => authFetchVersion<'prove-v4'>(),
    [authFetchVersion]
  )
  const authFetchLabV2 = useCallback(
    () => authFetchVersion<'lab-v2'>(),
    [authFetchVersion]
  )

  const access = useQuery({
    queryKey: ['effective-access', userId],
    enabled: isAuthenticated && !!userId,
    queryFn: async () => {
      const { data } = await authFetch().GET('/users/me')
      if (!data) throw new Error('Unable to load application access')
      return data
    },
    // UI hints may be stale; every backend mutation checks current permissions.
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false
  })
  const roles = access.data?.roles ?? (access.isError ? noRoles : undefined)
  const permissions =
    access.data?.permissions ?? (access.isError ? noPermissions : undefined)
  const isAdmin = roles?.includes('admin') ?? false
  const accessPending = isAuthenticated && access.isPending
  const accessError = isAuthenticated && access.isError && access.data == null
  const accessRefreshing = access.isFetching
  const retryAccess = access.refetch

  return (
    <AuthContext.Provider
      value={React.useMemo(
        () => ({
          loginRequest: props.loginRequest,
          logout,
          isAuthenticated,
          accountName,
          isAdmin,
          roles,
          permissions,
          accessPending,
          accessError,
          accessRefreshing,
          retryAccess,
          userId,
          getAccessToken,
          authenticatedFetch,
          authFetch,
          authFetchV2,
          authFetchV3,
          authFetchV4,
          authFetchLabV2
        }),
        [
          props.loginRequest,
          logout,
          isAuthenticated,
          accountName,
          isAdmin,
          roles,
          permissions,
          accessPending,
          accessError,
          accessRefreshing,
          retryAccess,
          userId,
          getAccessToken,
          authenticatedFetch,
          authFetch,
          authFetchV2,
          authFetchV3,
          authFetchV4,
          authFetchLabV2
        ]
      )}
    >
      {props.children}
    </AuthContext.Provider>
  )
}

const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext)
  if (context) {
    return context
  }
  throw new Error('useAuth must be used within an AuthProvider')
}
export { AuthProvider, useAuth }
